import * as THREE from "three";
import { Camera } from "./Camera";
import { State } from "../entities/OfferItem";
import { SilverModelSkeleton } from "./models/skeleton/SilverModelSkeleton";
import { BioclimaticModelSkeleton } from "./models/skeleton/BioclimaticModelSkeleton";
import { CoralModelSkeleton } from "./models/skeleton/CoralModelSkeleton";
import { PickHandler } from "./PickHandler";
import { GetDimensions } from "./Dimensions";
import { SidePicker } from "./SidePicker";
import { Ground } from "./Ground";
import { SizeHelper } from "./SizeHelper";
import { Wall } from "./Wall";
import { PerimeterLedLightExtra } from "./extras/PerimeterLedLightExtra";
import { PostProcessing } from "./PostProcessing";
import { PvcLedLightsExtra } from "./extras/PvcLedLightsExtra";
import { SceneLights } from "./SceneLights";
import { MaterialPicker } from "./MaterialPicker";
import { Sprites } from "./Sprites";
export class Scene {
    constructor(canvasElement, renderer, setSelectedSide = undefined, updateFormData = undefined) {
        this.threeScene = new THREE.Scene();
        this.requestAnimationId = null;
        this.canvasElement = canvasElement;
        this.renderer = renderer;
        this.setSelectedSide = setSelectedSide;
        this.updateFormData = updateFormData;
        this.renderListenerFunction = this.renderOnDemand.bind(this);
    }
    /**
     * Builds the scene with the specified parameters of the user's input.
     * Uses width and projection to calculate the number of modules to show.
     * 1. Creates the ground mesh
     * 2. Builds the meshes of the product
     * 3. Creates lights and the camera
     * @param formData
     */
    build(formData) {
        this.formData = formData;
        this.onDemandRendering = true;
        this.dimensions = new GetDimensions(this.formData, this).dimensions;
        this.materialPicker = new MaterialPicker(this);
        this.camera = new Camera(this);
        this.postProcessing = new PostProcessing(this);
        this.ground = new Ground(this);
        this.sizeHelper = new SizeHelper(this);
        this.pickHandler = new PickHandler(this.camera, this.canvasElement, this);
        this.pvcLedLights = new PvcLedLightsExtra(this);
        new PerimeterLedLightExtra(this);
        this.lightSource = new SceneLights(this);
        new Sprites(this);
        this.createModules();
        this.enableAxisHelper(false);
        this.wall = new Wall(this);
        this.sidePicker = new SidePicker(this);
        this.renderOnDemand();
        // We invoke the renderOnDemand function when the camera changes
        this.camera.controls.addEventListener('change', this.renderListenerFunction);
        // Captures the render frame
        let dataUrl = this.renderer.domElement.toDataURL("image/jpeg");
        this.formData.modelImage = dataUrl;
    }
    /**
     * Rebuils the objects in the scene without disposing the scene. Further optimalization needed
     * **/
    rebuild(formData) {
        this.formData = formData;
        if (formData.state !== State.WALLSELECTING && formData.state !== State.EXTRASELECTING)
            this.onDemandRendering = true;
        else
            this.onDemandRendering = false;
        this.dimensions = new GetDimensions(this.formData, this).dimensions;
        this.materialPicker = new MaterialPicker(this);
        this.ground = new Ground(this);
        this.sizeHelper = new SizeHelper(this);
        this.pickHandler = new PickHandler(this.camera, this.canvasElement, this);
        this.pvcLedLights = new PvcLedLightsExtra(this);
        this.enableAxisHelper(false);
        new PerimeterLedLightExtra(this);
        this.lightSource = new SceneLights(this);
        new Sprites(this);
        this.createModules();
        this.camera.updateCamPos(this.formData);
        this.wall = new Wall(this);
        // Captures the render frame
        this.renderConfig();
        let dataUrl = this.renderer.domElement.toDataURL("image/jpeg");
        this.formData.modelImage = dataUrl;
        this.sidePicker = new SidePicker(this);
    }
    /**
     * Add or remove the camera event listeners according to the renderer's type and invoke the renderer
     * **/
    renderConfig() {
        if (this.onDemandRendering === false && this.camera.controls.hasEventListener('change', this.renderListenerFunction) === true) {
            this.camera.controls.removeEventListener('change', this.renderListenerFunction);
        }
        else if (this.camera.controls.hasEventListener('change', this.renderListenerFunction) === false) {
            this.camera.controls.addEventListener('change', this.renderListenerFunction);
        }
        if (this.onDemandRendering === true) {
            this.stopRender();
            this.renderOnDemand();
        }
        else {
            this.render();
        }
    }
    /**
     * Update loop - only running when we need raycast
     * **/
    render() {
        this.requestAnimationId = requestAnimationFrame(this.render.bind(this));
        if (this.pickHandler)
            this.pickHandler.raycastHandler();
        this.renderOnDemand();
    }
    renderOnDemand() {
        this.renderer.autoClearDepth = false;
        this.camera.threeCamera.layers.set(1);
        this.postProcessing.composer.render();
        this.renderer.clearDepth();
        this.camera.threeCamera.layers.set(0);
        this.renderer.clearDepth();
        this.renderer.render(this.threeScene, this.camera.threeCamera);
    }
    /**
     * Creates meshes of the modules based on form data.
     * 4 = Silver
     * 5 = Coral
     * else Bioclimatic
     */
    createModules() {
        switch (this.formData.measuredModel.model.id) {
            // Silver
            case 4:
                new SilverModelSkeleton(this);
                break;
            // Coral
            case 5:
                new CoralModelSkeleton(this);
                break;
            // Bioclimatic
            default:
                new BioclimaticModelSkeleton(this);
                break;
        }
    }
    // returns the corresponding size to the string
    getActualSide(name) {
        switch (name) {
            case "LEFT": {
                return this.formData.left;
            }
            case "RIGHT": {
                return this.formData.right;
            }
            case "CLOSE": {
                return this.formData.close;
            }
            case "FAR": {
                return this.formData.far;
            }
        }
    }
    resizeRendererToDisplaySize(renderer) {
        const canvas = renderer.domElement;
        const pixelRatio = window.devicePixelRatio;
        const width = canvas.clientWidth * pixelRatio | 0;
        const height = canvas.clientHeight * pixelRatio | 0;
        const needResize = canvas.width !== width || canvas.height !== height;
        if (needResize) {
            renderer.setSize(width, height, false);
        }
        return needResize;
    }
    resize() {
        if (this.resizeRendererToDisplaySize(this.renderer)) {
            this.camera.resize();
            if (this.onDemandRendering === true)
                this.renderOnDemand();
        }
    }
    /** Removes the scene's objects and event listeners **/
    disposeAll() {
        this.pickHandler.removeEventListeners();
        this.sceneTraverse(this.threeScene, o => {
            if (o.geometry) {
                o.geometry.dispose();
            }
            if (o.material) {
                if (o.material.length > 0) {
                    for (let i = 0; i < o.material.length; ++i) {
                        if (o.material[i]) {
                            if (o.material[i].map) {
                                o.material[i].map.dispose();
                                o.material[i].map = undefined;
                            }
                            if (o.material[i].normalMap) {
                                o.material[i].normalMap.dispose();
                                o.material[i].normalMap = undefined;
                            }
                            if (o.material[i].roughnessMap) {
                                o.material[i].roughnessMap.dispose();
                                o.material[i].roughnessMap = undefined;
                            }
                            if (o.material[i].aoMap) {
                                o.material[i].aoMap.dispose();
                                o.material[i].aoMap = undefined;
                            }
                            if (o.material[i].alphaMap) {
                                o.material[i].alphaMap.dispose();
                                o.material[i].alphaMap = undefined;
                            }
                            o.material[i].dispose();
                            o.material[i] = undefined;
                        }
                    }
                }
                else {
                    if (o.material.map) {
                        o.material.map.dispose();
                        o.material.map = undefined;
                    }
                    if (o.material.normalMap) {
                        o.material.normalMap.dispose();
                        o.material.normalMap = undefined;
                    }
                    if (o.material.roughnessMap) {
                        o.material.roughnessMap.dispose();
                        o.material.roughnessMap = undefined;
                    }
                    if (o.material.aoMap) {
                        o.material.aoMap.dispose();
                        o.material.aoMap = undefined;
                    }
                    if (o.material.alphaMap) {
                        o.material.alphaMap.dispose();
                        o.material.alphaMap = undefined;
                    }
                    o.material.dispose();
                    o.material = undefined;
                }
            }
            o = undefined;
        });
        this.threeScene.clear();
    }
    sceneTraverse(obj, fn) {
        if (!obj)
            return;
        fn(obj);
        if (obj.children && obj.children.length > 0) {
            obj.children.forEach(o => {
                this.sceneTraverse(o, fn);
            });
        }
    }
    stopRender() {
        if (this.requestAnimationId) {
            cancelAnimationFrame(this.requestAnimationId);
        }
        this.requestAnimationId = undefined;
    }
    assignTexture(map, texture) {
        const textureData = this.renderer.properties.get(texture);
        if (!textureData.__webglTexture)
            this.renderer.initTexture(texture);
        const mapData = this.renderer.properties.get(map);
        Object.assign(mapData, textureData);
    }
    enableAxisHelper(enable) {
        if (!enable)
            return;
        const axesHelper = new THREE.AxesHelper(800);
        axesHelper.position.set(0, 0, 0);
        this.threeScene.add(axesHelper);
    }
}
